import {
  Component,
  effect,
  inject,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  SimpleChanges,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { notEmptyOrSpaces } from '@sbd-doug-frontend/shared/lib/public-validators';
import { SharedModule } from '@sbd-account-portal/app/shared/shared.module';
import { SharedComponentsModule } from '@sbd-doug-frontend/shared/lib/shared-components/shared-components.module';
import { GenericType, stateLabelValues } from '@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types';
import { combineLatest, debounceTime, map, Observable, startWith, Subject, takeUntil, tap } from 'rxjs';
import { CIPXQuoteRequestIntakeFormService } from '../../intake-form.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  businessTypes,
  CIPXQuoteRequestIntakeForm,
  CIPXQuoteRequestIntakeFormBondFamily,
  CIPXQuoteRequestIntakeFormBondFamilyGroup,
  CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption,
  CIPXQuoteRequestIntakeFormStateBond,
  IntakeFormStep,
} from '../../intake-form.types';
import {
  configureFormValidationByStateBondRequirements,
  configureIntakeFormByPricingConfiguration,
  getQuestionsForSection,
  isCIPXQuoteRequestIntakeFormBondFamily,
  isGenericType,
  sectionHasQuestions,
} from '../../intake-form.helpers';
import { stages, updateStageConfig, findStageConfig } from './stage-config';
import { isStageActive } from './stage-signals';
import { BondLookupUIControlStage } from './intake-form-bond-lookup.types';
import { CurrencyPipe } from '@angular/common';
import { CIPXQuoteRequestIntakeFormQuestionSetComponent } from '../../question-set/question-set.component';
import { MatRadioChange } from '@angular/material/radio';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { CathyService } from '@sbd-account-portal/app/services/cathy.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { LoggingService } from '@sbd-doug-frontend/shared/lib/shared-services/logging.service';

@Component({
  standalone: true,
  imports: [SharedModule, SharedComponentsModule, CIPXQuoteRequestIntakeFormQuestionSetComponent, NgxMaskDirective],
  providers: [CurrencyPipe, provideNgxMask()],
  selector: 'cipx-quote-request-intake-form-bond-lookup',
  templateUrl: 'intake-form-bond-lookup.component.html',
})
export class CIPXQuoteRequestIntakeFormBondLookupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() quoteRequestForm!: FormGroup<CIPXQuoteRequestIntakeForm>;
  @Output() complete = new EventEmitter<boolean>();

  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _currencyPipe = inject(CurrencyPipe);
  private _intakeFormService = inject(CIPXQuoteRequestIntakeFormService);
  private _cathyService = inject(CathyService);
  private _unsubscribeAll = new Subject<void>();
  private _isValidating = false; // Flag to prevent cascade during validation
  private _isInitializing = true; // Flag to prevent resets during component initialization
  private _loggingService = inject(LoggingService);

  businessTypes = businessTypes;

  BondLookupUIControlStage = BondLookupUIControlStage;
  isStageActive = isStageActive;

  states = stateLabelValues;

  filteredStates$!: Observable<GenericType[]>;
  filteredBondFamilies!: Observable<CIPXQuoteRequestIntakeFormBondFamily[]>;
  filteredStateBonds$!: Observable<CIPXQuoteRequestIntakeFormStateBond[]>;

  bondFamiliesLoading = this._intakeFormService.bondFamiliesLoading;
  bondFamilies = this._intakeFormService.bondFamilies;
  groupedBondFamilies: CIPXQuoteRequestIntakeFormBondFamilyGroup[] = [];
  filteredBondFamilyGroups$!: Observable<CIPXQuoteRequestIntakeFormBondFamilyGroup[]>;

  stateBondsLoading = this._intakeFormService.stateBondsLoading;
  stateBonds = this._intakeFormService.stateBonds;
  selectedStateBond = this._intakeFormService.selectedStateBond;

  pricingCalculatorConfiguration = this._intakeFormService.pricingCalculatorConfiguration;
  pricingCalculatorConfigurationLoading = this._intakeFormService.pricingCalculatorConfigurationLoading;

  penaltyOptionsLoading = this._intakeFormService.penaltyOptionsLoading;
  penaltyOptions = this._intakeFormService.penaltyOptions;

  selectedPenaltyOption = this._intakeFormService.selectedPenaltyOption;

  // Function bindings
  sectionHasQuestions = sectionHasQuestions;

  /**
   * Check if additional questions controls are properly set up for the given section
   */
  additionalQuestionsControlsReady(sectionName: string, stateBond: CIPXQuoteRequestIntakeFormStateBond | null): boolean {
    if (!stateBond || !sectionHasQuestions(sectionName, stateBond)) {
      return false;
    }

    const additionalQuestionsGroup = this.quoteRequestForm.controls.additional_questions;
    return getQuestionsForSection(sectionName, stateBond).every(
      question => additionalQuestionsGroup.get(question.question_code) !== null
    );
  }

  ngOnInit() {
    this.setupStreams(this.quoteRequestForm);

    // Set up bond amount value transformation
    this.setupBondAmountTransformation();

    // Allow resets after initial setup is complete
    setTimeout(() => {
      this._isInitializing = false;
    }, 100);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteRequestForm'] && this.quoteRequestForm) {
      //this.setupStreams(this.quoteRequestForm);
    }
  }

  onCompanyNumberOwnersChange(value: MatButtonToggleChange) {
    this._intakeFormService.companyNumberOwnersChanged.emit(value.value);
  }

  onCompanyTypeIdChanged(value: MatRadioChange) {
    if (value.value === 3) {
      this.quoteRequestForm.controls.requestDetails.controls.company_number_owners.setValue(1);
      this._intakeFormService.companyNumberOwnersChanged.emit(1);
    }

    this._intakeFormService.companyTypeIdChanged.emit(value.value);
  }

  stageComplete() {
    this._loggingService.log('🚀 Bond lookup stageComplete() called');

    // Set flag to prevent cascade during validation
    this._isValidating = true;

    // Debug: Check actual form values
    const bondLookupGroup = this.quoteRequestForm.controls.bondLookup;
    this._loggingService.log('📝 Current form values:');
    this._loggingService.log('  - stateSelection:', bondLookupGroup.controls.stateSelection.value);
    this._loggingService.log('  - bondFamilySelection:', bondLookupGroup.controls.bondFamilySelection.value);
    this._loggingService.log('  - stateBondSelection:', bondLookupGroup.controls.stateBondSelection.value);
    this._loggingService.log('  - bond_amount:', bondLookupGroup.controls.bond_amount.value);
    this._loggingService.log('  - company_type_id:', bondLookupGroup.controls.company_type_id.value);
    this._loggingService.log('  - bond_family_label:', bondLookupGroup.controls.bond_family_label.value);
    // Debug: Check control states
    this._loggingService.log('🔍 Control states:');
    Object.keys(bondLookupGroup.controls).forEach((key) => {
      const control = bondLookupGroup.get(key);
      if (control) {
        this._loggingService.log(
          `  - ${key}: valid=${control.valid}, value=${JSON.stringify(control.value)}, errors=`,
          control.errors
        );
      }
    });

    // Get detailed validation info for debugging
    const validationDetails = this._intakeFormService.getValidationDetails(
      IntakeFormStep.BondLookup,
      this.quoteRequestForm,
      this.selectedStateBond()
    );

    this._loggingService.log('📋 Validation details:', validationDetails);

    // Debug: Check current stage states
    this._loggingService.log('🎭 Current stage states:');
    this._loggingService.log('  - State:', isStageActive(BondLookupUIControlStage.State));
    this._loggingService.log('  - BondFamily:', isStageActive(BondLookupUIControlStage.BondFamily));
    this._loggingService.log('  - StateBond:', isStageActive(BondLookupUIControlStage.StateBond));
    this._loggingService.log('  - Penalty:', isStageActive(BondLookupUIControlStage.Penalty));
    this._loggingService.log('  - PenaltyOptions:', isStageActive(BondLookupUIControlStage.PenaltyOptions));
    console.log('  - BondSelectionCompleted:', isStageActive(BondLookupUIControlStage.BondSelectionCompleted));

    // Validate bond lookup step before proceeding
    const isValid = this._intakeFormService.validateCurrentStep(
      IntakeFormStep.BondLookup,
      this.quoteRequestForm,
      this.selectedStateBond()
    );

    // Clear validation flag
    this._isValidating = false;

    if (isValid === true) {
      this._loggingService.log('✅ Validation passed, proceeding to next step');
      this.complete.emit(true);
    } else {
      this._loggingService.log('❌ Validation failed, showing error and staying on current step');
      this._cathyService.error('Please fill in all required fields for the bond lookup step.');
      // Don't reset stages or emit anything that might cause a reset
      // Just show the error and let the user fix the issues
    }
  }

  toggleStage(stage: BondLookupUIControlStage): void {
    // Locate the stage configuration for the requested stage.
    const stageConfig = findStageConfig(stage, stages);
    if (stageConfig) {
      // Toggle the state: get the current state and reverse it.
      const newState = !isStageActive(stage);
      updateStageConfig(stageConfig, newState);
    }
  }

  isActive(stage: BondLookupUIControlStage): boolean {
    return isStageActive(stage);
  }

  private setupBondAmountTransformation() {
    const bondAmountControl = this.quoteRequestForm.controls.bondLookup.controls.bond_amount;

    // Subscribe to value changes to convert string to number
    bondAmountControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value: any) => {
      if (typeof value === 'string' && value.trim()) {
        // Convert the masked string to a number (remove commas)
        const numericValue = parseFloat(value.replace(/,/g, ''));
        if (!isNaN(numericValue)) {
          // Update the control value without triggering valueChanges again
          bondAmountControl.setValue(numericValue, { emitEvent: false });
        }
      }
    });
  }

  private setupStreams(form: FormGroup<CIPXQuoteRequestIntakeForm>) {
    const bondLookupGroup = form.controls.bondLookup;
    // Only set default company type if not already defined
    const existingCompanyType = bondLookupGroup.controls.company_type_id.value;
    if (existingCompanyType === null || existingCompanyType === undefined || existingCompanyType === '') {
      bondLookupGroup.controls.company_type_id.setValue(3);
    }

    const stateControl = bondLookupGroup.controls.stateSelection;
    const bondFamilyControl = bondLookupGroup.controls.bondFamilySelection;
    const stateBondControl = bondLookupGroup.controls.stateBondSelection;

    this.quoteRequestForm.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300)).subscribe((value) => {
      if (isStageActive(BondLookupUIControlStage.Penalty)) {
        this._loggingService.log('we at penalty value change', value);
      }

      return value;
    });

    this.filteredStates$ = stateControl.valueChanges.pipe(
      startWith(stateControl.value || ''),
      takeUntil(this._unsubscribeAll),
      map((value) => {
        this._loggingService.log('🔄 State control value changed:', value);

        // Only reset if this is a user-initiated change, not during validation or initialization
        if (!this._isValidating && !this._isInitializing) {
          bondFamilyControl.setValue('');
          this._loggingService.log('📍 Resetting stages: BondFamily, StateBond, Penalty, PenaltyOptions to false');
          const bondFamilyConfig = findStageConfig(BondLookupUIControlStage.BondFamily, stages);
          if (bondFamilyConfig) {
            updateStageConfig(bondFamilyConfig, false);
          }
          const stateBondConfig = findStageConfig(BondLookupUIControlStage.StateBond, stages);
          if (stateBondConfig) {
            updateStageConfig(stateBondConfig, false);
          }
          const penaltyConfig = findStageConfig(BondLookupUIControlStage.Penalty, stages);
          if (penaltyConfig) {
            updateStageConfig(penaltyConfig, false);
          }
          const penaltyOptionsConfig = findStageConfig(BondLookupUIControlStage.PenaltyOptions, stages);
          if (penaltyOptionsConfig) {
            updateStageConfig(penaltyOptionsConfig, false);
          }
          const writeInConfig = findStageConfig(BondLookupUIControlStage.WriteIn, stages);
          if (writeInConfig) {
            updateStageConfig(writeInConfig, false);
            this.setWriteInValidators(false);
          }
        } else {
          this._loggingService.log('⏸️ Skipping reset during validation or initialization');
        }

        return this._filterStates(value);
      })
    );

    this.filteredBondFamilyGroups$ = combineLatest([
      bondFamilyControl.valueChanges.pipe(startWith('')),
      stateControl.valueChanges.pipe(startWith('')),
    ]).pipe(
      takeUntil(this._unsubscribeAll),
      map(([bondFamilyValue]) => {
        // this._loggingService.log('🔄 Bond family control value changed:', bondFamilyValue);

        // Don't reset during validation or initialization to prevent cascade
        if (!this._isValidating && !this._isInitializing) {
          stateBondControl.setValue('');
          // console.log('📍 Resetting stages: StateBond, Penalty, PenaltyOptions to false');
          const stateBondConfig = findStageConfig(BondLookupUIControlStage.StateBond, stages);
          if (stateBondConfig) {
            updateStageConfig(stateBondConfig, false);
          }
          const penaltyConfig = findStageConfig(BondLookupUIControlStage.Penalty, stages);
          if (penaltyConfig) {
            updateStageConfig(penaltyConfig, false);
          }
          const penaltyOptionsConfig = findStageConfig(BondLookupUIControlStage.PenaltyOptions, stages);
          if (penaltyOptionsConfig) {
            updateStageConfig(penaltyOptionsConfig, false);
          }
        } else {
          // console.log('⏸️ Skipping reset during validation or initialization');
        }

        const filtered = this._filterBondFamilies(bondFamilyValue);
        return this._groupBondFamilies(filtered);
      })
    );

    this.filteredStateBonds$ = stateBondControl.valueChanges.pipe(
      startWith(stateBondControl.value || ''),
      takeUntil(this._unsubscribeAll),
      map((value) => {
        // console.log('🔄 State bond control value changed:', value);

        // Don't reset during validation or initialization to prevent cascade
        if (!this._isValidating && !this._isInitializing) {
          // console.log('📍 Resetting stage: BondSelectionCompleted to false');
          const stateBondConfig = findStageConfig(BondLookupUIControlStage.BondSelectionCompleted, stages);
          if (stateBondConfig) {
            updateStageConfig(stateBondConfig, false);
          }
        } else {
          // console.log('⏸️ Skipping reset during validation or initialization');
        }

        return this._filterStateBonds(value);
      })
    );
  }

  _groupBondFamilies(
    bondFamilies: CIPXQuoteRequestIntakeFormBondFamily[]
  ): CIPXQuoteRequestIntakeFormBondFamilyGroup[] {
    const groups: { [key: string]: CIPXQuoteRequestIntakeFormBondFamily[] } = {};
    bondFamilies.forEach((bondFamily) => {
      if (!groups[bondFamily.grp]) {
        groups[bondFamily.grp] = [];
      }
      groups[bondFamily.grp].push(bondFamily);
    });

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map((groupName) => ({
        name: groupName,
        bondFamilies: groups[groupName],
      }));
  }

  onStateSelected(state: MatAutocompleteSelectedEvent) {
    const selection: GenericType = state.option.value;

    const bondFamilyConfig = findStageConfig(BondLookupUIControlStage.BondFamily, stages);
    if (bondFamilyConfig) {
      updateStageConfig(bondFamilyConfig, true);
    }

    // this.toggleStage(BondLookupUIControlStage.BondFamily);

    this._intakeFormService.getBondFamiliesByStateAbbreviation(selection.value).subscribe();
  }

  stateSelectionDisplayFn(input: GenericType | null): string {
    return input && input.label ? input.label : '';
  }

  onBondFamilySelected(bondFamily: MatAutocompleteSelectedEvent) {
    if (
      !isGenericType(this.quoteRequestForm.controls.bondLookup.controls.stateSelection.value) ||
      !isCIPXQuoteRequestIntakeFormBondFamily(bondFamily.option.value)
    ) {
      return;
    }

    const stateAbbreviation: GenericType = this.quoteRequestForm.controls.bondLookup.controls.stateSelection.value;
    this.quoteRequestForm.controls.state_abbreviation.setValue(stateAbbreviation.value);

    this._intakeFormService
      .getStateBondsByStateAbbreviationAndSlug(stateAbbreviation.value, bondFamily.option.value.slug)
      .subscribe((response) => {
        this._loggingService.log('🔄 State bonds response:', response);

                        // If we only have one response its most likely "Not listed here.". Automatically preselect it and then
        // enable the "Write In" stage fields.
        if (response.length === 1) {
          this._loggingService.log('🔄 Only one state bond found, auto-selecting it');

          // Set the selectedStateBond to the first (and only) item
          const firstStateBond = response[0];

          // IMPORTANT: configure dynamic additional_questions controls before anything renders that binds them
          configureFormValidationByStateBondRequirements(this.quoteRequestForm, firstStateBond);
          // Trigger change detection after form controls are configured
          this._changeDetectorRef.detectChanges();

          this._intakeFormService.selectedStateBond.set(firstStateBond);

          // Also set the form control value so the autocomplete shows the selection
          this.quoteRequestForm.controls.bondLookup.controls.stateBondSelection.setValue(firstStateBond);
          this.quoteRequestForm.controls.state_bond_id.setValue(firstStateBond.state_bond_id);

          const writeInConfig = findStageConfig(BondLookupUIControlStage.WriteIn, stages);
          if (writeInConfig) {
            this._loggingService.log('🔄 Setting WriteIn stage to true');
            updateStageConfig(writeInConfig, true);
            this._changeDetectorRef.detectChanges();
          } else {
            this._loggingService.log('🔄 No writeInConfig found');
          }

          // Check if the auto-selected state bond is a write-in bond (state_bond_id === 1)
          if (firstStateBond.state_bond_id === 1) {
            this.setWriteInValidators(true);
            const bondSelectionCompletedConfig = findStageConfig(BondLookupUIControlStage.BondSelectionCompleted, stages);
            if (bondSelectionCompletedConfig) {
              this._loggingService.log('🔄 Enabling BondSelectionCompleted stage for auto-selected write-in bond');
              updateStageConfig(bondSelectionCompletedConfig, true);
              this._changeDetectorRef.detectChanges();
            }
          }

          const penaltyConfig = findStageConfig(BondLookupUIControlStage.Penalty, stages);
          if (penaltyConfig) {
            updateStageConfig(penaltyConfig, true);
          }
        }
        // if (response.data.state_bonds.state_bonds.length === 0) {
        //   const writeInConfig = findStageConfig(BondLookupUIControlStage.WriteIn, stages);
        //   if (writeInConfig) {
        //     updateStageConfig(writeInConfig, true);
        //   }
        // }
      });
    const stateBondConfig = findStageConfig(BondLookupUIControlStage.StateBond, stages);
    if (stateBondConfig) {
      updateStageConfig(stateBondConfig, true);
    }
    // this.toggleStage(BondLookupUIControlStage.StateBond);
  }

  bondFamilySelectionDisplayFn(input: CIPXQuoteRequestIntakeFormBondFamily | null): string {
    return input && input.bond_family_label ? input.bond_family_label : '';
  }

  onStateBondSelected(event: MatAutocompleteSelectedEvent) {
    configureFormValidationByStateBondRequirements(this.quoteRequestForm, event.option.value);
    console.log('configured question controls', this.quoteRequestForm.controls.additional_questions);

    // Trigger change detection after form controls are configured
    this._changeDetectorRef.detectChanges();

    // this._prepareOptions(event.option.value);
    this.quoteRequestForm.controls.state_bond_id.setValue(event.option.value.state_bond_id);
    this._intakeFormService.selectedStateBond.set(event.option.value);

        // Check if the selected state bond has state_bond_id = 1, enable write-in
    if (event.option.value.state_bond_id === 1) {
      this._loggingService.log('🔄 State bond has family ID 1, enabling WriteIn stage');
      const writeInConfig = findStageConfig(BondLookupUIControlStage.WriteIn, stages);
      if (writeInConfig) {
        updateStageConfig(writeInConfig, true);
        this.setWriteInValidators(true);
        this._changeDetectorRef.detectChanges();
      }

      // Also enable BondSelectionCompleted for write-in bonds
      const bondSelectionCompletedConfig = findStageConfig(BondLookupUIControlStage.BondSelectionCompleted, stages);
      if (bondSelectionCompletedConfig) {
        this._loggingService.log('🔄 Enabling BondSelectionCompleted stage for write-in bond');
        updateStageConfig(bondSelectionCompletedConfig, true);
        this._changeDetectorRef.detectChanges();
      }
    } else {
      // For non-write-in bonds, ensure WriteIn stage is disabled and validators are cleared
      const writeInConfig = findStageConfig(BondLookupUIControlStage.WriteIn, stages);
      if (writeInConfig) {
        updateStageConfig(writeInConfig, false);
        this.setWriteInValidators(false);
        this._loggingService.log('🔄 Disabled WriteIn stage for non-write-in bond');
      }
    }

    // TODO: Handle when we get no response back.
    this._intakeFormService.getPricingCalculatorConfiguration(event.option.value).subscribe({
      next: (response) => {
        const penaltyConfig = findStageConfig(BondLookupUIControlStage.Penalty, stages);
        if (penaltyConfig) {
          updateStageConfig(penaltyConfig, true);
        }

        configureIntakeFormByPricingConfiguration(this.quoteRequestForm, response);

        if (
          response.penalty.penalty_input_type === 4 &&
          response.penalty.options &&
          response.penalty.options.length > 0
        ) {
          if (this.penaltyOptions() !== null) {
            const firstOption = this.penaltyOptions()!.at(0);
            if (firstOption) {
              this.selectPricingOption(firstOption);
            }
          }
          const penaltyOptionsConfig = findStageConfig(BondLookupUIControlStage.PenaltyOptions, stages);
          if (penaltyOptionsConfig) {
            updateStageConfig(penaltyOptionsConfig, true);
          }
        }

        if (response.penalty.penalty_input_type === 1 || response.penalty.penalty_input_type === 3) {
          const bondSelectionCompletedConfig = findStageConfig(BondLookupUIControlStage.BondSelectionCompleted, stages);
          if (bondSelectionCompletedConfig) {
            updateStageConfig(bondSelectionCompletedConfig, true);
          }
        }

        // this.toggleStage(BondLookupUIControlStage.Penalty);
      },
      error: (err) => {
        const penaltyConfig = findStageConfig(BondLookupUIControlStage.Penalty, stages);
        if (penaltyConfig) {
          updateStageConfig(penaltyConfig, false);
        }
      },
    });
  }

  stateBondSelectionDisplayFn(input: CIPXQuoteRequestIntakeFormStateBond | null): string {
    return input && input.surety_bond_label ? input.surety_bond_label : (input?.default_surety_bond_label ?? '');
  }

  private _filterStates(value: null | string | GenericType): GenericType[] {
    if (value === null) {
      return this.states;
    }

    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();

      if (!filterValue) {
        return this.states;
      }
      return this.states.filter((state) => state.label.toLowerCase().includes(filterValue));
    }
    return this.states.filter((state) => state === value);
  }

  private _filterBondFamilies(
    value: null | string | CIPXQuoteRequestIntakeFormBondFamily
  ): CIPXQuoteRequestIntakeFormBondFamily[] {
    if (value === null) {
      return this.bondFamilies();
    }

    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();

      if (!filterValue) {
        return this.bondFamilies();
      }
      return this.bondFamilies().filter((bondFamily) =>
        bondFamily.bond_family_label.toLowerCase().includes(filterValue)
      );
    }
    return this.bondFamilies().filter((bondFamily) => bondFamily === value);
  }

  private _filterStateBonds(
    value: null | string | CIPXQuoteRequestIntakeFormStateBond
  ): CIPXQuoteRequestIntakeFormStateBond[] {
    if (value === null) {
      return this.stateBonds();
    }

    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();

      if (!filterValue) {
        return this.stateBonds();
      }
      return this.stateBonds().filter((stateBond) => {
        if (stateBond && stateBond.default_surety_bond_label) {
          return stateBond.default_surety_bond_label.toLowerCase().includes(filterValue);
        }
        return false;
      });
    }
    return this.stateBonds().filter((stateBond) => stateBond === value);
  }

  selectPenaltyOptionRadio(change: MatRadioChange) {
    this.selectPricingOption(change.value);
  }

  selectPricingOption(option: CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption) {
    this.quoteRequestForm.controls.bondLookup.controls.selectedPenaltyOption.setValue(option);
    this.selectedPenaltyOption.set(option);
    const bondSelectionCompletedConfig = findStageConfig(BondLookupUIControlStage.BondSelectionCompleted, stages);
    if (bondSelectionCompletedConfig) {
      updateStageConfig(bondSelectionCompletedConfig, true);
    }

    this._changeDetectorRef.detectChanges();
  }

  getPenaltyOptions() {
    if (this.selectedStateBond() === null) {
      return;
    }

    this._intakeFormService.calculatePenaltyOptions(this.quoteRequestForm, this.selectedStateBond()!).subscribe({
      next: (response) => {
        const penaltyConfig = findStageConfig(BondLookupUIControlStage.Penalty, stages);
        if (penaltyConfig) {
          updateStageConfig(penaltyConfig, true);
        }

        // configureIntakeFormByPricingConfiguration(this.quoteRequestForm, response);

        if (
          [4, 6].includes(response.penalty.penalty_input_type) &&
          response.penalty.options &&
          response.penalty.options.length > 0
        ) {
          if (this.penaltyOptions() !== null) {
            const firstOption = this.penaltyOptions()!.at(0);
            if (firstOption) {
              this.selectPricingOption(firstOption);
            }
          }

          const penaltyOptionsConfig = findStageConfig(BondLookupUIControlStage.PenaltyOptions, stages);
          if (penaltyOptionsConfig) {
            updateStageConfig(penaltyOptionsConfig, true);
          }
        }

        const bondSelectionCompletedConfig = findStageConfig(BondLookupUIControlStage.BondSelectionCompleted, stages);
        if (bondSelectionCompletedConfig) {
          updateStageConfig(bondSelectionCompletedConfig, true);
        }

        // this.toggleStage(BondLookupUIControlStage.Penalty);
      },
      error: (err) => {
        const penaltyConfig = findStageConfig(BondLookupUIControlStage.Penalty, stages);
        if (penaltyConfig) {
          updateStageConfig(penaltyConfig, false);
        }
      },
    });
  }

  /**
   * Sets or removes required validators for write-in fields
   */
  private setWriteInValidators(required: boolean): void {
    const bondLookupGroup = this.quoteRequestForm.controls.bondLookup;
    const bondFamilyLabelControl = bondLookupGroup.controls.bond_family_label;
    const suretyBondUserNotesControl = bondLookupGroup.controls.surety_bond_user_notes;

    if (required) {
      // Add required validators with notEmptyOrSpaces to prevent whitespace-only entries
      bondFamilyLabelControl.setValidators([Validators.required, notEmptyOrSpaces]);
      suretyBondUserNotesControl.setValidators([Validators.required, notEmptyOrSpaces]);
      this._loggingService.log('🔄 Added required and notEmptyOrSpaces validators for write-in fields');
    } else {
      // Remove validators
      bondFamilyLabelControl.clearValidators();
      suretyBondUserNotesControl.clearValidators();
      this._loggingService.log('🔄 Cleared validators for write-in fields');
    }

    // Update validity after changing validators
    bondFamilyLabelControl.updateValueAndValidity();
    suretyBondUserNotesControl.updateValueAndValidity();
  }
}
